import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import attendanceService from '../../services/AttendanceService.js';
import axiosInstance from '../../config/axios.js';
import { Calendar, Eye, Pencil, Trash2 } from 'lucide-react';
import {
  PageLayout,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const AttendanceList = () => {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAgentOrDriver =
    currentUser.role === 'Agent' ||
    currentUser.role === 'Driver' ||
    currentUser.role_name === 'Agent' ||
    currentUser.role_name === 'Driver';

  const [attendances, setAttendances] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({
    user_id: '',
    start_date: '',
    end_date: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });
  const [periodDisplay, setPeriodDisplay] = useState('');
  const navigate = useNavigate();
  const perPage = 10;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchAttendances(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  useEffect(() => {
    fetchAttendances(currentPage);
  }, [currentPage]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users?all=true');
      setUsers(response.data.data || []);
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  const fetchAttendances = async (page = currentPage) => {
    try {
      setLoading(true);

      if (filterValues.user_id) {
        const response = await attendanceService.getUserAttendances(
          filterValues.user_id,
          page,
          perPage,
          filterValues.start_date || null,
          filterValues.end_date || null
        );
        setAttendances(response.data || []);
        setTotalPages(response.pagination?.pages || 1);
      } else {
        const response = await attendanceService.getAllUsersAttendanceSummary(
          filterValues.year,
          filterValues.month
        );
        setAttendances(response.data || []);
        setTotalPages(1);
        setPeriodDisplay(response.period || '');
      }
    } catch (err) {
      Alert.error('Failed to load attendance records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleDelete = async (attendanceId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to delete this attendance record? This action cannot be undone.',
      'Delete Attendance Record'
    );

    if (confirmed) {
      try {
        await axiosInstance.delete(`/attendance/${attendanceId}`);
        Alert.success('Attendance record deleted successfully!');

        if (attendances.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchAttendances();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete attendance record');
      }
    }
  };

  const detailColumns = [
    {
      key: 'attendance_date',
      label: 'Date',
      render: (a) => formatDate(a.attendance_date),
    },
    {
      key: 'login_time',
      label: 'Login Time',
      render: (a) => formatTime(a.login_time),
    },
    {
      key: 'logout_time',
      label: 'Logout Time',
      render: (a) =>
        a.is_logged_out ? (
          formatTime(a.logout_time)
        ) : (
          <StatusBadge status="active" label="Active" />
        ),
    },
    { key: 'work_hours_formatted', label: 'Work Hours' },
    {
      key: 'login_location',
      label: 'Login Location',
      render: (a) =>
        a.login_location
          ? `${a.login_location.city}, ${a.login_location.state}`
          : 'N/A',
    },
    {
      key: 'logout_location',
      label: 'Logout Location',
      render: (a) =>
        a.logout_location
          ? `${a.logout_location.city}, ${a.logout_location.state}`
          : 'N/A',
    },
  ];

  const summaryColumns = [
    {
      key: 'name',
      label: 'User',
      render: (summary) => (
        <button
          onClick={() => navigate(`/attendance/report/${summary.user_id}`)}
          className="font-medium text-brand hover:underline text-left"
        >
          {summary.name}
        </button>
      ),
    },
    { key: 'role_name', label: 'Role' },
    {
      key: 'is_online',
      label: 'Online Status',
      render: (summary) => (
        <StatusBadge status={summary.is_online ? 'active' : 'inactive'} label={summary.is_online ? 'Online' : 'Offline'} />
      ),
    },
    { key: 'days_worked', label: 'Days Worked' },
    { key: 'days_absent', label: 'Days Absent' },
    { key: 'total_work_hours_formatted', label: 'Total Hours' },
  ];

  const getDetailActions = (attendance) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/attendance/view/${attendance.id}`),
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () => navigate(`/attendance/edit/${attendance.id}`),
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(attendance.id),
    },
  ];

  const getSummaryActions = (summary) => [
    {
      label: 'View Report',
      icon: Eye,
      onClick: () => navigate(`/attendance/report/${summary.user_id}`),
    },
  ];

  const filterConfig = [
    ...(!isAgentOrDriver
      ? [
          {
            key: 'user_id',
            label: 'User',
            type: 'select',
            options: users.map((u) => ({ value: u.user_id, label: u.name })),
          },
        ]
      : []),
    { key: 'start_date', label: 'Start Date', type: 'date' },
    { key: 'end_date', label: 'End Date', type: 'date' },
    ...(!filterValues.user_id
      ? [
          {
            key: 'month',
            label: 'Month',
            type: 'select',
            options: monthNames.map((month, index) => ({
              value: index + 1,
              label: month,
            })),
          },
          { key: 'year', label: 'Year', type: 'number' },
        ]
      : []),
  ];

  const pageTitle = filterValues.user_id
    ? 'Attendance Records'
    : periodDisplay
      ? `Attendance Records - ${periodDisplay}`
      : 'Attendance Records';

  return (
    <PageLayout title={pageTitle} icon={Calendar}>
      <FilterPanel
        filters={filterConfig}
        values={filterValues}
        onChange={setFilterValues}
        onClear={() =>
          setFilterValues({
            user_id: '',
            start_date: '',
            end_date: '',
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
          })
        }
      />

      <DataTable
        columns={filterValues.user_id ? detailColumns : summaryColumns}
        data={attendances}
        loading={loading}
        emptyMessage="No attendance records found"
        rowKey={filterValues.user_id ? 'id' : 'user_id'}
        actions={filterValues.user_id ? getDetailActions : getSummaryActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
};

export default AttendanceList;
